/* eslint-disable react/prop-types */
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';

import { createMakeModelApi } from '../../../../components/make-model/api-helpers';

const MakeModelApprovalPanelContext = createContext();

// Turns a `{ makeName, makeCode }` / `{ modelName, modelCode }` seed from the
// row being edited into the `{ label, value }` shape the shared TypeAhead uses.
const toMakeOption = (seed) =>
  seed?.makeName
    ? { label: seed.makeName, value: seed.makeCode }
    : undefined;
const toModelOption = (seed) =>
  seed?.modelName
    ? { label: seed.modelName, value: seed.modelCode }
    : undefined;

export function MakeModelApprovalPanelProvider({
  allowCustom = true,
  initialState,
  showChassis: defaultShowChassis = false,
  requiresChassis = true,
  children,
}) {
  const { getToken } = useCurrentUser();

  // Make-model editing hits the CMME ("Catalog Make-Model Edit") context.
  const { makesFetchOptionsPromise, modelsFetchOptionsPromise } = useMemo(
    () => createMakeModelApi('CMME'),
    [],
  );

  const fetchMakes = useCallback(
    async (input, signal) => {
      const token = await getToken();
      return makesFetchOptionsPromise(input, token, signal);
    },
    [getToken],
  );

  const fetchModels = useCallback(
    async (input, makeCode, signal) => {
      const token = await getToken();
      return modelsFetchOptionsPromise(input, makeCode, token, signal);
    },
    [getToken],
  );

  const showChassis = defaultShowChassis;

  const bodyMakeSeed = toMakeOption(initialState?.bodyMake);
  const [bodyMake, setBodyMake] = useState(bodyMakeSeed);
  const [bodyMakeInputValue, setBodyMakeInputValue] = useState(
    bodyMakeSeed?.label || '',
  );
  const [bodyMakeOptions, setBodyMakeOptions] = useState([]);

  const bodyModelSeed = toModelOption(initialState?.bodyModel);
  const [bodyModel, setBodyModel] = useState(bodyModelSeed);
  const [bodyModelInputValue, setBodyModelInputValue] = useState(
    bodyModelSeed?.label || '',
  );
  const [bodyModelOptions, setBodyModelOptions] = useState([]);

  const chassisMakeSeed = toMakeOption(initialState?.chassisMake);
  const [chassisMake, setChassisMake] = useState(chassisMakeSeed);
  const [chassisMakeInputValue, setChassisMakeInputValue] = useState(
    chassisMakeSeed?.label || '',
  );
  const [chassisMakeOptions, setChassisMakeOptions] = useState([]);

  const chassisModelSeed = toModelOption(initialState?.chassisModel);
  const [chassisModel, setChassisModel] = useState(chassisModelSeed);
  const [chassisModelInputValue, setChassisModelInputValue] = useState(
    chassisModelSeed?.label || '',
  );
  const [chassisModelOptions, setChassisModelOptions] = useState([]);

  const [comment, setComment] = useState('');

  const [dirtyFields, setDirtyFields] = useState([]);

  const getErrors = useCallback(() => {
    const requiredMsg = 'This is a required field';
    const errors = [];

    const requireField = (selected, inputValue, field) => {
      if (selected) return;
      if (allowCustom) {
        if (!inputValue) {
          errors.push({ field, message: requiredMsg });
        }
      } else {
        errors.push({ field, message: requiredMsg });
      }
    };

    requireField(bodyMake, bodyMakeInputValue, 'bodyMake');
    requireField(bodyModel, bodyModelInputValue, 'bodyModel');

    if (showChassis && requiresChassis) {
      requireField(chassisMake, chassisMakeInputValue, 'chassisMake');
      requireField(chassisModel, chassisModelInputValue, 'chassisModel');
    }

    return errors;
  }, [
    allowCustom,
    showChassis,
    requiresChassis,
    bodyMake,
    bodyMakeInputValue,
    bodyModel,
    bodyModelInputValue,
    chassisMake,
    chassisMakeInputValue,
    chassisModel,
    chassisModelInputValue,
  ]);

  const value = {
    allowCustom,
    showChassis,
    requiresChassis,

    bodyMake,
    setBodyMake,
    bodyMakeInputValue,
    setBodyMakeInputValue,
    bodyMakeOptions,
    setBodyMakeOptions,

    bodyModel,
    setBodyModel,
    bodyModelInputValue,
    setBodyModelInputValue,
    bodyModelOptions,
    setBodyModelOptions,

    chassisMake,
    setChassisMake,
    chassisMakeInputValue,
    setChassisMakeInputValue,
    chassisMakeOptions,
    setChassisMakeOptions,

    chassisModel,
    setChassisModel,
    chassisModelInputValue,
    setChassisModelInputValue,
    chassisModelOptions,
    setChassisModelOptions,

    comment,
    setComment,

    fetchMakes,
    fetchModels,

    getErrors,
    dirtyFields,
    setDirtyFields,
  };

  return (
    <MakeModelApprovalPanelContext.Provider value={value}>
      {children}
    </MakeModelApprovalPanelContext.Provider>
  );
}

MakeModelApprovalPanelProvider.propTypes = {
  allowCustom: PropTypes.bool,
  showChassis: PropTypes.bool,
  requiresChassis: PropTypes.bool,
  initialState: PropTypes.shape({
    bodyMake: PropTypes.object,
    bodyModel: PropTypes.object,
    chassisMake: PropTypes.object,
    chassisModel: PropTypes.object,
  }),
  children: PropTypes.node.isRequired,
};

export function useMakeModelApprovalPanelProvider() {
  const context = useContext(MakeModelApprovalPanelContext);
  if (!context) {
    throw new Error(
      'useMakeModelApprovalPanelProvider must be used within a MakeModelApprovalPanelProvider',
    );
  }
  return context;
}

export default MakeModelApprovalPanelProvider;
